<template>
  <div v-click-outside="onBlur" class="typeahead-container">
  <!-- div v-click-outside="onBlur" class="typeahead-container"-->
    <div class="typeahead">
      <div
        class="input-group combobox"
        role="combobox"
        aria-haspopup="listbox"
        :aria-owns="`${id}-list`"
        :aria-expanded="showValues ? 'true' : 'false'"
      >
        <input
          type="text"
          class="form-control"
          :class="{'is-invalid': isInvalid}"
          ref="typeaheadInput"
          :id="id"

          @input="onInput"
          @keydown="onKeyPressed"

          v-model="inputValue"

          aria-autocomplete="both"
          :placeholder="placeholder"
          :aria-label="ariaLabel"
          :aria-describedby="ariaDescribedby"
          :aria-required="ariaRequired"
          :aria-activedescendant="showValues ? `${id}-item-${activeDropdownValueIndex}` : null"
          :aria-controls="`${id}-list`"
        />
        <div class="action-container d-flex align-items-center justify-content-center">
          <div class="action-spinner" v-if="searching"><LoadingSpinner /></div>
          <som-button
            v-else-if="inputValue.length > 0"
            button-type="clear"
            @click="clearInputValue"
            aria-label="Clear search"
          >
            <som-icon size="sm" name="icon-cancel" color="gray-600" />
          </som-button>
        </div>
        <div class="input-group-append">
          <button ref="typeaheadButton" class="btn btn-primary btn-icon" type="button" @click="searchItems">
            <som-icon name="icon-search" size="sm" color="white"></som-icon>
          </button>
        </div>
      </div>
      <div class="dropdown-container">
        <ul
          :id="`${id}-list`"
          class="list-group"
          :class="{hidden: !showValues}"
          role="listbox"
        >
          <li
            v-for="(item, i) in values"
            :key="`${id}-item-${i}`"
            :id="`${id}-item-${i}`"
            class="list-group-item"
            :class="{'active': activeItem.id === item.id}"
            role="option"
            :aria-selected="activeItem.id === item.id"
            @click="() => onClickItem(item)"
          >
            <div class="title">{{item.name}}</div>
            <div v-if="item.id !== null" class="subtitle">{{item.address}} <br />{{item.city}}, {{item.state}}, {{item.zipCode}}</div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from './LoadingSpinner';

export default {
  name: 'Typeahead',
  components: {
    LoadingSpinner
  },
  props: {
    id: {
      type: String,
      required: true
    },
    searching: {
      type: Boolean,
      default: false,
    },
    search: {
      type: Function,
      required: true
    },
    dropdownValues: {
      type: Array,
      default: () => []
    },
    selectedDropdownValue: {
      type: Object,
      default: null
    },
    onSelectClearInput: {
      type: Boolean,
      default: false
    },
    onSelectDropdownValue: {
      type: Function,
      default: () => {}
    },
    placeholder: {
      type: String,
    },
    ariaLabel: {
      type: String
    },
    ariaRequired: {
      type: Boolean
    },
    ariaDescribedby: {
      type: String
    },
    icon: {
      type: String,
      default: "icon-search"
    },
    isInvalid: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeDropdownValueIndex: 0,
      inputValue: '',
      values: [],
    }
  },
  computed: {
    activeItem() {
      return this.values[this.activeDropdownValueIndex];
    },
    showValues() {
      return this.values.length > 0;
    }
  },
  methods: {
    focus() {
      this.$refs.typeaheadInput.focus();
    },
    closeDropdown() {
      this.activeDropdownValueIndex = 0;
      this.values = [];
    },
    selectItem(item) {
      this.inputValue = this.onSelectClearInput ? '' : item.title;
      this.closeDropdown();

      this.onSelectDropdownValue(item);
    },
    searchItems() {
      this.search(this.inputValue);
    },
    onInput() {
      this.searchItems(this.inputValue);
    },
    onButtonClick() {
      this.$refs.typeaheadInput.focus();
      this.searchItems(this.inputValue);
    },
    onKeyPressed(e) {
      if (e.keyCode === 13) {
        // Enter key was pressed
        e.preventDefault();
        e.stopPropagation();
        this.onEnterKey();
      } else if (e.keyCode === 40) {
        // Arrow down key was pressed
        this.onDownKey();
      } else if (e.keyCode === 38) {
        // Arrow up key was pressed
        this.onUpKey();
      } else if (e.keyCode === 27 && this.showValues) {
        // ESC key pressed and dropdown values are displayed
        // IE doesn't get the @keydown.esc, so we need to do it
        e.preventDefault();
        e.stopPropagation();
        this.onEscKey();
      } else if (e.keyCode === 9) {
        // TAB key was pressed (either tab or shif-tab), let's close our menu
        this.closeDropdown();
      }
    },
    onDownKey() {
      if (!this.showValues) {
        // Dropdown is hidden. Run search again with what's in the text input.
        this.searchItems(this.inputValue);
        return;
      }

      if (this.activeDropdownValueIndex === this.values.length - 1) {
        this.activeDropdownValueIndex = 0;
      } else {
        this.activeDropdownValueIndex++
      }
    },
    onUpKey() {
      if (this.activeDropdownValueIndex === 0) {
        this.activeDropdownValueIndex = this.values.length - 1;
      } else {
        this.activeDropdownValueIndex--;
      }
    },
    onClickItem(item) {
      this.selectItem(item);
    },
    onEnterKey() {
      const item = this.values[this.activeDropdownValueIndex];
      if (item) {
        this.selectItem(item);
      } 
    },
    onEscKey() {
      this.closeDropdown();
    },
    onBlur() {
      this.closeDropdown();
    },
    clearInputValue() {
      this.inputValue = '';
      this.focus();
    }
  },
  watch: {
    inputValue(newValue, oldValue) {
      if (oldValue.length > 0 && newValue.length === 0) {
        // Content erased
        this.closeDropdown();
      }
    },
    dropdownValues(newValues) {
      const isCurrentlyFocusedInput = document.activeElement === this.$refs.typeaheadInput;
      const isCurrentlyFocusedButton = document.activeElement === this.$refs.typeaheadButton;
      if (isCurrentlyFocusedInput || isCurrentlyFocusedButton) {
        // Make sure to show the dropdown menu only if input is focused
        this.activeDropdownValueIndex = 0;
        this.values = newValues;
      }
    }
  },
  created() {
    this.inputValue = this.selectedDropdownValue ? this.selectedDropdownValue.title : '';
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.typeahead-container {
  width: 100%;
  max-height: 20rem;
}

.typeahead {
  width: 100%;
  position: relative;
  display: inline-block;
}
input {
  padding-right: 3rem;
}

input::-ms-clear {
  display: none;
}

.input-group-append {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 4;
}

.dropdown-container {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 1000;
}

.hidden {
  display: none;
}

.list-group {
  -ms-overflow-style: none;
  max-height: 20rem;
  overflow: scroll;
  border: 1px solid rgba(0,0,0,.125);
}

.list-group-item {
  background-color: #fff;
  border: 0;
  margin-bottom: 0;
  border-top: 1px solid rgba(0,0,0,.125);
}

.list-group-item:hover {
  cursor: pointer;
}

.list-group-item:hover,
.list-group-item.active {
  background-color: #eef3f6;
  color: #000;
}

.list-group-item:first-child {
  border-top: 0;
}

.title {
  font-size: 0.9rem;
  font-weight: 700;
}

.subtitle {
  font-size: 0.85rem;
}

.action-container {
  position: absolute;
  top: 0px;
  height: 100%;
  right: 42px;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-spinner {
  margin: 0 0.75rem;
  width: 20px;
}
</style>
